import { useState, useEffect, useCallback, useRef } from "react";
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { doc, onSnapshot, collection, query, orderBy, limit, type FirestoreError } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";
import { Badge, Button, Callout, Icon, IconButton, Logo, Spinner, cn, type LucideIcon } from "@winche/ui";
import {
  ArrowDownUp,
  ChartColumn,
  ChartLine,
  CircleDollarSign,
  LayoutDashboard,
  Monitor,
  Moon,
  Search,
  Settings,
  Sun,
  TrendingUp,
  Users,
  Zap,
  Accessibility,
} from "lucide-react";
import { SignInScreen } from "./components/SignInScreen";
import { SectionErrorBoundary } from "./components/section-error-boundary";
import { checkAdminClaim, describeCallableError, failureTitle, type AdminFailure } from "./lib/callable-error";
import { useAdminTheme } from "./lib/theme";
import type {
  AdminTokenSummary,
  AdminMonetizationSummary,
  AnnouncementBanner,
  ModelReasoningSetting,
  AdminSection,
} from "@winche/shared";
import {
  AI_DAILY_ALERT_BUDGET_USD,
  DEFAULT_AI_MODEL_ID,
  parseTokenUsageMaps,
  parseAdminSectionHash,
  adminSectionHash,
} from "@winche/shared";

import {
  OverviewSection,
  type AdminModelDailyBudget,
  type AdminPseoRefreshCurrent,
  type AdminSystemAlerts,
  type CoverageByCountryDoc,
  type CrawlerHealthDoc,
} from "./sections/OverviewSection";
import { AiSpendSection } from "./sections/AiSpendSection";
import { SimulatorSection } from "./sections/SimulatorSection";
import { BenchmarkSection } from "./sections/BenchmarkSection";
import { UsersSection, type UserTokenStat, type AdminUserInsight } from "./sections/UsersSection";
import { FunnelSection, type AdminFunnelStats, type AdminAttentionReport } from "./sections/FunnelSection";
import { MonetizationSection, type AdminBillingStats } from "./sections/MonetizationSection";
import { NetBillingSection } from "./sections/NetBillingSection";
import { AccessibilitySection, type A11yReportDoc } from "./sections/AccessibilitySection";
import { SeoSection, type SeoRunDoc } from "./sections/SeoSection";
import { SettingsSection } from "./sections/SettingsSection";

/** A model id field of config/global, or null when missing or empty. */
function configModelId(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** `config/global.announcementBanner`, or null when absent or malformed. */
function parseAnnouncementBanner(value: unknown): AnnouncementBanner | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as { enabled?: unknown; textEn?: unknown; textEs?: unknown };
  return {
    enabled: raw.enabled === true,
    textEn: typeof raw.textEn === "string" ? raw.textEn : "",
    textEs: typeof raw.textEs === "string" ? raw.textEs : "",
  };
}

const ADMIN_UID = import.meta.env.VITE_ADMIN_UID;
const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL;

function hydrateAdminTokenSummary(raw: Record<string, unknown>): AdminTokenSummary {
  const parsed = parseTokenUsageMaps(raw);
  return {
    ...(raw as unknown as AdminTokenSummary),
    monthlyAiCost: parsed.monthlyAiCost,
    operationCounts: parsed.operationCounts as AdminTokenSummary["operationCounts"],
    operationStats: parsed.operationStats as AdminTokenSummary["operationStats"],
    modelCosts: parsed.modelCosts as unknown as AdminTokenSummary["modelCosts"],
  };
}

/** VITE_ADMIN_* only identify the bootstrap account; they never grant access by themselves. */
function isBootstrapAdmin(user: User): boolean {
  if (ADMIN_EMAIL && user.emailVerified && user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) return true;
  return Boolean(ADMIN_UID) && user.uid === ADMIN_UID;
}

/**
 * Admin means the `admin` custom claim — the same check Firestore rules and
 * every admin callable make, so passing this gate never ends in empty panels
 * from permission-denied. The bootstrap account without the claim yet asks
 * `claimAdminBootstrap` for it once, then refreshes its token. A rejection
 * means the check could not run (network, cold start), not "no claim": the
 * gate offers Retry instead of saying the account is not an admin
 * (ux-admin-11).
 */
async function resolveAdminClaim(user: User): Promise<boolean> {
  const token = await user.getIdTokenResult(true);
  if (token.claims.admin === true) return true;
  if (!isBootstrapAdmin(user)) return false;
  await httpsCallable(functions, "claimAdminBootstrap")();
  const refreshed = await user.getIdTokenResult(true);
  return refreshed.claims.admin === true;
}

// ─── Cloud Function callables ─────────────────────────────────────────────────

const getAdminUserStats        = httpsCallable<void, { users: UserTokenStat[] }>(functions, "getAdminUserStats");
const getAdminBillingStats     = httpsCallable<void, AdminBillingStats>(functions, "getAdminBillingStats");
const getAdminFunnelFn         = httpsCallable<void, AdminFunnelStats>(functions, "getAdminFunnel");
const getAdminAttentionFn      = httpsCallable<void, AdminAttentionReport>(functions, "getAdminAttention");
const getAdminUsersInsightsFn  = httpsCallable<void, { users: AdminUserInsight[] }>(functions, "getAdminUsersInsights");
const reconcileMonetizationFn  = httpsCallable<Record<string, never>, { success: boolean; skipped?: boolean }>(functions, "reconcileMonetization");
// `acknowledgeFreeTierRetention`: the operator confirmed the free-tier warning (ux-admin-02).
const setActiveModelFn            = httpsCallable<{ modelId: string; acknowledgeFreeTierRetention?: boolean }, { success: boolean; activeModelId: string; previousModelId: string | null }>(functions, "setActiveModel");
const setActiveVisionModelFn      = httpsCallable<{ modelId: string; acknowledgeFreeTierRetention?: boolean }, { success: boolean; activeVisionModelId: string; previousModelId: string | null }>(functions, "setActiveVisionModel");
const setPaidFallbackModelFn      = httpsCallable<{ modelId: string | null; kind?: "text" | "vision" }, { success: boolean }>(functions, "setPaidFallbackModel");
const setUseFreeModelFn          = httpsCallable<{ enabled: boolean; kind?: "text" | "vision"; acknowledgeFreeTierRetention?: boolean }, { success: boolean }>(functions, "setUseFreeModel");
const setModelReasoningEffortFn  = httpsCallable<{ modelId: string; effort: ModelReasoningSetting | null }, { success: boolean }>(functions, "setModelReasoningEffort");
const setBenchmarkCustomModelsFn  = httpsCallable<{ textModels: object[]; visionModels: object[] }, { success: boolean }>(functions, "setBenchmarkCustomModels");
const setAnnouncementBannerFn     = httpsCallable<AnnouncementBanner, { success: boolean }>(functions, "setAnnouncementBanner");
const clearAdminSystemAlertFn     = httpsCallable<{ key: string }, { success: boolean; cleared: boolean }>(functions, "clearAdminSystemAlert");

// ─── Navigation ───────────────────────────────────────────────────────────────

type Section = AdminSection;

type NavItem = { id: Section; label: string; icon: LucideIcon };

/** Grouped like Stripe/Vercel (admin-09): monitor, money, tools; Settings last. */
const NAV_GROUPS: { label: string | null; items: NavItem[] }[] = [
  {
    label: "Monitor",
    items: [
      { id: "overview", label: "Overview", icon: LayoutDashboard },
      { id: "funnel", label: "Analytics", icon: TrendingUp },
      { id: "users", label: "Users", icon: Users },
      { id: "seo", label: "SEO", icon: Search },
      { id: "accessibility", label: "Accessibility", icon: Accessibility },
    ],
  },
  {
    label: "Money",
    items: [
      { id: "monetization", label: "Monetization", icon: CircleDollarSign },
      { id: "billing", label: "Billing Trend", icon: ChartLine },
      { id: "aispend", label: "AI Spend", icon: Zap },
    ],
  },
  {
    label: "Tools",
    items: [
      { id: "benchmark", label: "Benchmark", icon: ArrowDownUp },
      { id: "simulator", label: "Simulator", icon: ChartColumn },
    ],
  },
  { label: null, items: [{ id: "settings", label: "Settings", icon: Settings }] },
];

const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** Form-like sections read in a narrow column; data views use the width (admin-M1). */
const NARROW_SECTIONS: ReadonlySet<Section> = new Set<Section>(["settings", "simulator"]);

function ThemeButton({ theme }: { theme: ReturnType<typeof useAdminTheme> }) {
  const { preference, cycle } = theme;
  const icon = preference === "light" ? Sun : preference === "dark" ? Moon : Monitor;
  const label = `Theme: ${preference} (click to change)`;
  return <IconButton label={label} variant="ghost" className="md:pointer-fine:size-8" onClick={cycle}><Icon icon={icon} size="sm" /></IconButton>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const theme = useAdminTheme();
  const [user, setUser]       = useState<User | null | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  /** The claim check failed (not "no claim"): the gate shows it with Retry. */
  const [claimError, setClaimError] = useState<AdminFailure | null>(null);
  const [checkingClaim, setCheckingClaim] = useState(false);
  const [section, setSection] = useState<Section>(() =>
    typeof window === "undefined" ? "overview" : parseAdminSectionHash(window.location.hash),
  );

  // Real-time data
  const [summary, setSummary]                       = useState<AdminTokenSummary | null>(null);
  const [monetization, setMonetization]             = useState<AdminMonetizationSummary | null>(null);
  const [crawlerHealth, setCrawlerHealth]           = useState<CrawlerHealthDoc | null>(null);
  const [coverage, setCoverage]                     = useState<CoverageByCountryDoc | null>(null);
  const [systemAlerts, setSystemAlerts]             = useState<AdminSystemAlerts | null>(null);
  const [aiDailyBudget, setAiDailyBudget]           = useState<number>(AI_DAILY_ALERT_BUDGET_USD);
  const [aiDailySeries, setAiDailySeries]           = useState<Record<string, number> | null>(null);
  const [anthropicDailySeries, setAnthropicDailySeries] = useState<Record<string, number> | null>(null);
  const [otherDailySeries, setOtherDailySeries]     = useState<Record<string, number> | null>(null);
  /** undefined until the listener answers, null when aiBudgetGuard has not published it yet. */
  const [modelBudget, setModelBudget]               = useState<AdminModelDailyBudget | null | undefined>(undefined);
  const [pseoRefresh, setPseoRefresh]               = useState<AdminPseoRefreshCurrent | null>(null);
  const [seoRuns, setSeoRuns]                       = useState<SeoRunDoc[] | null>(null);
  const [a11yReport, setA11yReport]                 = useState<A11yReportDoc | null | undefined>(null);
  const [freeForAll, setFreeForAll]                 = useState<boolean | null>(null);
  const [lsTestMode, setLsTestMode]                 = useState<boolean | null>(null);
  /** undefined until config/global answers (the banner form waits for it), null without a banner. */
  const [announcementBanner, setAnnouncementBanner] = useState<AnnouncementBanner | null | undefined>(undefined);
  const [evaluateJobModelId, setEvaluateJobModelId] = useState<string | null>(null);
  const [activeModelId, setActiveModelId]                   = useState<string | null>(null);
  const [activeVisionModelId, setActiveVisionModelId]       = useState<string | null>(null);
  const [paidFallbackModelId, setPaidFallbackModelId]             = useState<string | null>(null);
  const [paidFallbackVisionModelId, setPaidFallbackVisionModelId] = useState<string | null>(null);
  const [useFreeTextModel, setUseFreeTextModel]                   = useState<boolean | null>(null);
  const [useFreeVisionModel, setUseFreeVisionModel]               = useState<boolean | null>(null);
  const [reasoningEffortByModel, setReasoningEffortByModel]       = useState<Record<string, string> | null>(null);
  const [benchmarkCustomModels, setBenchmarkCustomModels]   = useState<object | null>(null);
  const [reconciling, setReconciling]                 = useState(false);
  const [reconciledJustNow, setReconciledJustNow]     = useState(false);

  // Firestore listener health: surface permission/network failures instead of
  // silently rendering empty sections, and self-heal once when the failure is
  // a stale ID token missing the `admin` custom claim.
  const [firestoreError, setFirestoreError]           = useState<string | null>(null);
  const [tokenEpoch, setTokenEpoch]                   = useState(0);
  const tokenRefreshAttempted                         = useRef(false);

  const selectSection = useCallback((next: Section) => {
    setSection(next);
    const hash = adminSectionHash(next);
    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", hash);
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => setSection(parseAdminSectionHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  // Listeners that failed in the current "failure episode". A rules/claim
  // failure hits every listener at once (and again after a token refresh +
  // re-subscribe), so only the first failure of an episode logs an error and
  // the rest warn — one error + banner instead of 14 identical errors. The
  // episode ends when a listener succeeds again.
  const failedListenersRef                            = useRef<Set<string>>(new Set());

  const onListenerError = useCallback((label: string, err: FirestoreError) => {
    const isEpisodeStart = failedListenersRef.current.size === 0;
    const notYetLogged = !failedListenersRef.current.has(label);
    failedListenersRef.current.add(label);
    if (isEpisodeStart) console.error(`[admin] ${label} listener failed`, err);
    else if (notYetLogged) console.warn(`[admin] listener also failed (${label})`, err);
    setFirestoreError((prev) => prev ?? `${label}: ${err.message}`);
    if (err.code === "permission-denied" && !tokenRefreshAttempted.current && auth.currentUser) {
      tokenRefreshAttempted.current = true;
      // Force-refresh the ID token so a newly granted admin claim reaches
      // Firestore rules, then re-subscribe all listeners via tokenEpoch.
      void auth.currentUser.getIdToken(true).then(
        () => setTokenEpoch((n) => n + 1),
        () => { /* refresh failed — the banner stays visible */ }
      );
    }
  }, []);

  // A listener recovering ends the failure episode (and hides the banner when
  // every previously-failing listener is healthy again).
  const onListenerSuccess = useCallback((label: string) => {
    if (failedListenersRef.current.delete(label) && failedListenersRef.current.size === 0) {
      setFirestoreError(null);
    }
  }, []);

  // On-demand data
  const [userStats, setUserStats]         = useState<UserTokenStat[] | null>(null);
  const [usersInsights, setUsersInsights] = useState<AdminUserInsight[] | null>(null);
  const [loadingUsers, setLoadingUsers]   = useState(false);
  const [usersError, setUsersError]       = useState<AdminFailure | null>(null);
  const [usersInsightsError, setUsersInsightsError] = useState<AdminFailure | null>(null);

  const [funnel, setFunnel]               = useState<AdminFunnelStats | null>(null);
  const [attention, setAttention]         = useState<AdminAttentionReport | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState<AdminFailure | null>(null);

  const [billingStats, setBillingStats]       = useState<AdminBillingStats | null>(null);
  const [loadingBilling, setLoadingBilling]   = useState(false);
  const [billingError, setBillingError]       = useState<AdminFailure | null>(null);

  // Custom claims (admin: true) only reach Firestore security rules when the
  // ID token refreshes; resolveAdminClaim forces a refresh so a recently
  // granted claim works without a manual sign-out/in cycle.
  const checkClaim = useCallback((u: User) => {
    setCheckingClaim(true);
    void checkAdminClaim(() => resolveAdminClaim(u)).then((check) => {
      setClaimError(check.status === "unverified" ? check.failure : null);
      setIsAdmin(check.status === "admin");
      setUser(u);
      setCheckingClaim(false);
    });
  }, []);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        if (u) {
          checkClaim(u);
        } else {
          setClaimError(null);
          setIsAdmin(false);
          setUser(u);
        }
      }),
    [checkClaim]
  );

  // Re-subscribe all Firestore listeners whenever the ID token changes
  // (a freshly granted admin claim, or the ~1h auto-refresh): security rules
  // evaluate the token at subscription time, so a new token must re-create
  // the listeners. Without this, granting the claim mid-session required a
  // manual sign-out/in cycle.
  useEffect(() => {
    if (!user || !isAdmin) return;
    return onIdTokenChanged(auth, (u) => {
      if (u) setTokenEpoch((n) => n + 1);
    });
  }, [user, isAdmin]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "tokenUsageSummary"),
      (snap) => {
        onListenerSuccess("tokenUsageSummary");
        setSummary(snap.exists() ? hydrateAdminTokenSummary(snap.data() as Record<string, unknown>) : null);
      },
      (err) => onListenerError("tokenUsageSummary", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "monetizationSummary"),
      (snap) => {
        onListenerSuccess("monetizationSummary");
        setMonetization(snap.exists() ? (snap.data() as AdminMonetizationSummary) : null);
      },
      (err) => onListenerError("monetizationSummary", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  // Weekly axe count (a11y-21): read only while its section is open.
  useEffect(() => {
    if (!user || !isAdmin || section !== "accessibility") return;
    return onSnapshot(
      doc(db, "_admin", "a11yReport"),
      (snap) => {
        onListenerSuccess("a11yReport");
        setA11yReport(snap.exists() ? (snap.data() as A11yReportDoc) : undefined);
      },
      (err) => onListenerError("a11yReport", err)
    );
  }, [user, isAdmin, section, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "crawlerHealth"),
      (snap) => {
        onListenerSuccess("crawlerHealth");
        setCrawlerHealth(snap.exists() ? (snap.data() as CrawlerHealthDoc) : null);
      },
      (err) => onListenerError("crawlerHealth", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "coverageByCountry"),
      (snap) => {
        onListenerSuccess("coverageByCountry");
        setCoverage(snap.exists() ? (snap.data() as CoverageByCountryDoc) : null);
      },
      (err) => onListenerError("coverageByCountry", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "systemAlerts"),
      (snap) => {
        onListenerSuccess("systemAlerts");
        setSystemAlerts(snap.exists() ? (snap.data() as AdminSystemAlerts) : null);
      },
      (err) => onListenerError("systemAlerts", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "aiDailySeries"),
      (snap) => {
        onListenerSuccess("aiDailySeries");
        const data = snap.exists()
          ? (snap.data() as { series?: Record<string, number>; otherSeries?: Record<string, number>; anthropicSeries?: Record<string, number> })
          : null;
        setAiDailySeries(data?.series ?? null);
        setOtherDailySeries(data?.otherSeries ?? null);
        setAnthropicDailySeries(data?.anthropicSeries ?? null);
      },
      (err) => onListenerError("aiDailySeries", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  // Today's Anthropic cap ("Haiku budget today", ux-admin-04), mirrored hourly by aiBudgetGuard.
  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "modelDailyBudget"),
      (snap) => {
        onListenerSuccess("modelDailyBudget");
        setModelBudget(snap.exists() ? (snap.data() as AdminModelDailyBudget) : null);
      },
      (err) => onListenerError("modelDailyBudget", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "pseoRefreshRuns"),
      (snap) => {
        onListenerSuccess("pseoRefreshRuns");
        setPseoRefresh(snap.exists() ? (snap.data() as AdminPseoRefreshCurrent) : null);
      },
      (err) => onListenerError("pseoRefreshRuns", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const runsRef = collection(db, "_admin", "seoReports", "runs");
    return onSnapshot(
      query(runsRef, orderBy("generatedAt", "desc"), limit(30)),
      (snap) => {
        onListenerSuccess("seoReports");
        setSeoRuns(snap.docs.map((d) => d.data() as SeoRunDoc));
      },
      (err) => onListenerError("seoReports", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "_admin", "config"),
      (snap) => {
        onListenerSuccess("_admin/config");
        const data = snap.exists() ? snap.data() : null;
        setLsTestMode(data?.lsTestMode ?? false);
      },
      (err) => onListenerError("_admin/config", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    return onSnapshot(
      doc(db, "config", "global"),
      (snap) => {
        onListenerSuccess("config/global");
        const data = snap.exists() ? snap.data() : null;
        setFreeForAll(data?.freeForAllMode ?? false);
        setAnnouncementBanner(parseAnnouncementBanner(data?.announcementBanner));
        // textModelId / visionModelId (the old active*ModelId fields are no longer read); missing → Haiku.
        setActiveModelId(configModelId(data?.textModelId) ?? DEFAULT_AI_MODEL_ID);
        setActiveVisionModelId(configModelId(data?.visionModelId) ?? DEFAULT_AI_MODEL_ID);
        // evaluate_job has its own model (kill switch); textModelId does not move it.
        setEvaluateJobModelId(configModelId(data?.evaluateJobModelId) ?? DEFAULT_AI_MODEL_ID);
        setPaidFallbackModelId(configModelId(data?.paidFallbackModelId));
        setPaidFallbackVisionModelId(configModelId(data?.paidFallbackVisionModelId));
        setUseFreeTextModel(data?.useFreeTextModel ?? true);
        setAiDailyBudget(typeof data?.aiDailyBudgetUsd === "number" ? data.aiDailyBudgetUsd : AI_DAILY_ALERT_BUDGET_USD);
        setUseFreeVisionModel(data?.useFreeVisionModel ?? true);
        setReasoningEffortByModel(data?.reasoningEffortByModel ?? {});
        setBenchmarkCustomModels(data?.benchmarkCustomModels ?? {});
      },
      (err) => onListenerError("config/global", err)
    );
  }, [user, isAdmin, tokenEpoch, onListenerError, onListenerSuccess]);

  useEffect(() => {
    if (section !== "overview" || !user || !isAdmin) return;
    // Only reconcile if monetization doc is missing or stale (not updated today UTC)
    const lastUpdatedAt = (monetization as unknown as { lastUpdatedAt?: { seconds: number } } | null)?.lastUpdatedAt;
    const todayUtcStart = new Date();
    todayUtcStart.setUTCHours(0, 0, 0, 0);
    const alreadyRanToday = lastUpdatedAt && lastUpdatedAt.seconds * 1000 >= todayUtcStart.getTime();
    if (alreadyRanToday) return;

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setReconciling(true);
      setReconciledJustNow(false);
      reconcileMonetizationFn({})
        .then((res) => {
          if (!cancelled && !res.data.skipped) setReconciledJustNow(true);
        })
        .catch(() => { /* silent — stale data is still shown */ })
        .finally(() => {
          if (!cancelled) setReconciling(false);
        });
    });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, user]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError(null);
    const [statsRes, insightsRes] = await Promise.allSettled([
      getAdminUserStats(),
      getAdminUsersInsightsFn(),
    ]);
    // A failed load drops what an earlier one left: Users reads null as "did
    // not load", so stale rows never sit under an error that says otherwise.
    if (statsRes.status === "fulfilled") {
      setUserStats(statsRes.value.data.users);
    } else {
      setUserStats(null);
      setUsersError(describeCallableError("getAdminUserStats", statsRes.reason));
    }
    if (insightsRes.status === "fulfilled") {
      setUsersInsights(insightsRes.value.data.users);
      setUsersInsightsError(null);
    } else {
      // Non-fatal: the token rows still render; the plan and onboarding
      // columns say they are unavailable (never "0 PRO", ux-admin-09).
      setUsersInsights(null);
      setUsersInsightsError(describeCallableError("getAdminUsersInsights", insightsRes.reason));
    }
    setLoadingUsers(false);
  }, []);

  const loadInsights = useCallback(async () => {
    setLoadingInsights(true);
    setInsightsError(null);
    const [funnelRes, attentionRes] = await Promise.allSettled([
      getAdminFunnelFn(),
      getAdminAttentionFn(),
    ]);
    // The first failure names its function; both failing usually share a cause.
    let failure: AdminFailure | null = null;
    if (funnelRes.status === "fulfilled") {
      setFunnel(funnelRes.value.data);
    } else {
      failure = describeCallableError("getAdminFunnel", funnelRes.reason);
    }
    if (attentionRes.status === "fulfilled") {
      setAttention(attentionRes.value.data);
    } else {
      failure ??= describeCallableError("getAdminAttention", attentionRes.reason);
    }
    if (failure) setInsightsError(failure);
    setLoadingInsights(false);
  }, []);

  const loadBilling = useCallback(async () => {
    setLoadingBilling(true);
    setBillingError(null);
    try {
      const res = await getAdminBillingStats();
      setBillingStats(res.data);
    } catch (err) {
      setBillingError(describeCallableError("getAdminBillingStats", err));
    } finally {
      setLoadingBilling(false);
    }
  }, []);

  // ── Loading state
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner size="md" label="Loading" />
      </div>
    );
  }

  // ── Sign-in (admin-18): the app's mark, a centered card, errors inline.
  if (!user) {
    return <SignInScreen />;
  }

  // ── The claim could not be checked (network, cold start): say so and retry,
  // never "not an admin account" (ux-admin-11).
  if (!isAdmin && claimError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sunken p-4">
        <div className="w-full max-w-[400px] rounded-xl border border-border bg-card p-6 text-center shadow-sm">
          <Logo variant="mark" size={40} className="mx-auto mb-4 justify-center" label="" />
          <h1 className="text-title font-semibold">Couldn&apos;t check admin access</h1>
          <p className="mt-1 text-label text-muted-foreground">
            The check for <span className="font-medium text-foreground">{user.email}</span> did not finish. That is a
            connection or server problem, not a missing permission.
          </p>
          <p className="mt-2 text-caption text-subtle-foreground">
            {failureTitle(claimError)}
            {claimError.detail ? ` — ${claimError.detail}` : ""}
          </p>
          <Button className="mt-5" fullWidth loading={checkingClaim} onClick={() => checkClaim(user)}>
            Retry
          </Button>
          <Button className="mt-2" fullWidth variant="outline" onClick={() => firebaseSignOut(auth).catch(console.error)}>
            Switch account
          </Button>
        </div>
      </div>
    );
  }

  // ── Access denied
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sunken p-4">
        <div className="w-full max-w-[360px] rounded-xl border border-border bg-card p-6 text-center shadow-sm">
          <Logo variant="mark" size={40} className="mx-auto mb-4 justify-center" label="" />
          <h1 className="text-title font-semibold">No admin access</h1>
          <p className="mt-1 text-label text-muted-foreground">
            <span className="font-medium text-foreground">{user.email}</span> is not an admin account.
          </p>
          <Button className="mt-5" fullWidth variant="outline" onClick={() => firebaseSignOut(auth).catch(console.error)}>
            Switch account
          </Button>
        </div>
      </div>
    );
  }

  const navButton = (item: NavItem, mobile: boolean) => {
    const active = section === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => selectSection(item.id)}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex items-center gap-2.5 rounded-md text-left text-label transition-colors duration-150",
          mobile ? "min-h-11 shrink-0 px-3" : "w-full px-2.5 py-2 md:pointer-fine:py-1.5",
          active ? "admin-nav-active" : "admin-nav-item",
        )}
      >
        <Icon icon={item.icon} size="sm" active={active} />
        {item.label}
      </button>
    );
  };

  // ── Main layout
  return (
    <div className="flex h-dvh overflow-hidden bg-background">

      {/* Sidebar — desktop */}
      <aside className="hidden h-dvh w-56 shrink-0 flex-col border-r border-border bg-sunken md:flex">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3.5">
          <Logo size={22} label="Winche" />
          <Badge tone="warning" size="sm">Admin</Badge>
        </div>

        <nav className="flex-1 overflow-y-auto p-2" aria-label="Sections">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.label ?? `g${gi}`} className={gi > 0 ? "mt-4" : undefined}>
              {group.label ? <p className="overline px-2.5 pb-1 text-subtle-foreground">{group.label}</p> : null}
              <div className="space-y-0.5">{group.items.map((item) => navButton(item, false))}</div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2">
            {user.photoURL && <img src={user.photoURL} alt="" className="h-6 w-6 shrink-0 rounded-full" />}
            <span className="flex-1 truncate text-caption text-muted-foreground">{user.displayName || user.email}</span>
            <ThemeButton theme={theme} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            className="mt-1 justify-start"
            onClick={() => firebaseSignOut(auth).catch(console.error)}
          >
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className="flex items-center gap-2 border-b border-border bg-sunken px-3 py-2 md:hidden"
          style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top, 0px))" }}
        >
          <Logo size={22} label="Winche" />
          <Badge tone="warning" size="sm">Admin</Badge>
          <div className="ml-auto flex items-center">
            <ThemeButton theme={theme} />
            <Button variant="ghost" size="sm" onClick={() => firebaseSignOut(auth).catch(console.error)}>
              Sign out
            </Button>
          </div>
        </div>
        <nav
          aria-label="Sections"
          className="relative flex gap-1 overflow-x-auto border-b border-border bg-sunken px-2 py-2 md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black_85%,transparent)]"
        >
          {NAV_ITEMS.map((item) => navButton(item, true))}
        </nav>

        <main className="flex-1 overflow-y-auto @container">
          <div className={cn("mx-auto p-4 md:p-6", NARROW_SECTIONS.has(section) ? "max-w-3xl" : "max-w-[1440px]")}>
          {firestoreError && (
            <Callout
              tone="danger"
              className="mb-4"
              title="Firestore listener failed"
              onDismiss={() => setFirestoreError(null)}
              dismissLabel="Dismiss"
            >
              {firestoreError}. If this is a permissions error, sign out and back in so your admin claim reaches
              Firestore rules.
            </Callout>
          )}
          <SectionErrorBoundary key={section} section={section} onGoToOverview={() => selectSection("overview")}>
          {section === "overview"     && <OverviewSection listenerFailed={firestoreError != null} summary={summary} monetization={monetization} crawlerHealth={crawlerHealth} coverage={coverage} systemAlerts={systemAlerts} pseoRefresh={pseoRefresh} reconciling={reconciling} reconciledJustNow={reconciledJustNow} activeModelId={activeModelId} activeVisionModelId={activeVisionModelId} paidFallbackModelId={paidFallbackModelId} paidFallbackVisionModelId={paidFallbackVisionModelId} evaluateJobModelId={evaluateJobModelId} modelBudget={modelBudget} reasoningEffortByModel={reasoningEffortByModel} onClearSystemAlert={async (key) => { await clearAdminSystemAlertFn({ key }); }} />}
          {section === "aispend"      && (
            <AiSpendSection
              summary={summary}
              freeForAll={freeForAll}
              users={userStats}
              loadingUsers={loadingUsers}
              usersError={usersError}
              onLoadUsers={loadUsers}
              dailySeries={aiDailySeries}
              anthropicDailySeries={anthropicDailySeries}
              otherDailySeries={otherDailySeries}
              dailyBudgetUsd={aiDailyBudget}
              modelBudget={modelBudget ?? null}
              listenerFailed={firestoreError != null}
            />
          )}
          {section === "funnel"       && <FunnelSection funnel={funnel} attention={attention} loading={loadingInsights} error={insightsError} onLoad={loadInsights} />}
          {section === "simulator"    && <SimulatorSection activeModelId={activeModelId ?? undefined} />}
          {section === "seo"          && <SeoSection runs={seoRuns} listenerFailed={firestoreError != null} />}
          {section === "accessibility" && <AccessibilitySection report={a11yReport} listenerFailed={firestoreError != null} />}
          {section === "benchmark"    && (
            benchmarkCustomModels === null
              ? <p className="text-muted-foreground">Loading benchmark configuration…</p>
              : <BenchmarkSection
              activeModelId={activeModelId ?? DEFAULT_AI_MODEL_ID}
              activeVisionModelId={activeVisionModelId ?? DEFAULT_AI_MODEL_ID}
              paidFallbackModelId={paidFallbackModelId}
              paidFallbackVisionModelId={paidFallbackVisionModelId}
              onSetActiveModel={async (modelId, acknowledgeFreeTierRetention) => { await setActiveModelFn({ modelId, acknowledgeFreeTierRetention }); }}
              onSetActiveVisionModel={async (modelId, acknowledgeFreeTierRetention) => { await setActiveVisionModelFn({ modelId, acknowledgeFreeTierRetention }); }}
              onSetPaidFallbackModel={async (modelId) => { await setPaidFallbackModelFn({ modelId, kind: "text" }); }}
              onSetPaidFallbackVisionModel={async (modelId) => { await setPaidFallbackModelFn({ modelId, kind: "vision" }); }}
              operationStats={summary?.operationStats}
              savedCustomModels={benchmarkCustomModels}
              onSaveCustomModels={async (textModels, visionModels) => { await setBenchmarkCustomModelsFn({ textModels, visionModels }); }}
            />
          )}
          {section === "users"        && <UsersSection stats={userStats} insights={usersInsights} loading={loadingUsers} error={usersError} insightsError={usersInsightsError} onLoad={loadUsers} />}
          {section === "monetization" && <MonetizationSection stats={billingStats} loading={loadingBilling} error={billingError} onLoad={loadBilling} />}
          {section === "billing"      && <NetBillingSection />}
          {section === "settings"     && (
            <SettingsSection
              freeForAll={freeForAll}
              lsTestMode={lsTestMode}
              announcementBanner={announcementBanner}
              activeModelId={activeModelId}
              activeVisionModelId={activeVisionModelId}
              paidFallbackModelId={paidFallbackModelId}
              paidFallbackVisionModelId={paidFallbackVisionModelId}
              useFreeTextModel={useFreeTextModel}
              useFreeVisionModel={useFreeVisionModel}
              reasoningEffortByModel={reasoningEffortByModel}
              onSetActiveModel={async (modelId, acknowledgeFreeTierRetention) => { await setActiveModelFn({ modelId, acknowledgeFreeTierRetention }); }}
              onSetActiveVisionModel={async (modelId, acknowledgeFreeTierRetention) => { await setActiveVisionModelFn({ modelId, acknowledgeFreeTierRetention }); }}
              onSetPaidFallbackModel={async (modelId) => { await setPaidFallbackModelFn({ modelId, kind: "text" }); }}
              onSetPaidFallbackVisionModel={async (modelId) => { await setPaidFallbackModelFn({ modelId, kind: "vision" }); }}
              onSetUseFreeTextModel={async (enabled, acknowledgeFreeTierRetention) => { await setUseFreeModelFn({ enabled, kind: "text", acknowledgeFreeTierRetention }); }}
              onSetUseFreeVisionModel={async (enabled, acknowledgeFreeTierRetention) => { await setUseFreeModelFn({ enabled, kind: "vision", acknowledgeFreeTierRetention }); }}
              onSetModelReasoning={async (modelId, effort) => { await setModelReasoningEffortFn({ modelId, effort }); }}
              onSetAnnouncementBanner={async (data) => { await setAnnouncementBannerFn(data); }}
            />
          )}
          </SectionErrorBoundary>
        </div>
      </main>
      </div>
    </div>
  );
}
